import React, { useState } from 'react';
import {
  ChevronRight,
  Clock,
  Bell,
  CalendarCheck,
  Sparkles,
  Heart,
  Users,
  Star,
  ShieldCheck,
  ChevronDown,
  ArrowRight,
  Stethoscope,
} from 'lucide-react';

/* ─── FAQ Data ─── */
const FAQ_ITEMS = [
  {
    q: '¿Realmente es gratis?',
    a: 'Sí, 100% gratis. Son minutos de consulta que se quedan sin usar y los ofrecemos a quienes más lo necesitan.',
  },
  {
    q: '¿Cómo sé cuándo hay una consulta disponible?',
    a: 'Te avisamos por WhatsApp o correo cuando haya un horario libre para ti.',
  },
  {
    q: '¿Puedo elegir la especialidad?',
    a: 'Sí, al registrarte eliges la especialidad que te interesa.',
  },
  {
    q: '¿Qué pasa si no puedo asistir?',
    a: 'Avísanos lo antes posible para darle tu lugar a alguien más.',
  },
  {
    q: '¿Necesito descargar alguna app?',
    a: 'No. Las consultas son por videollamada desde tu navegador en daisies.mx.',
  },
  {
    q: '¿Quién da las consultas?',
    a: 'Las mismas doctoras certificadas de Daisies, con experiencia en salud de mujeres y adolescentes.',
  },
];

/* ─── Testimonials Data ─── */
const TESTIMONIALS = [
  {
    name: 'Andrea, 19',
    location: 'Oaxaca',
    text: 'Nunca había tenido acceso a una psicóloga. Gracias a Daisies pude hablar con alguien por primera vez sobre lo que sentía.',
    emoji: '💜',
  },
  {
    name: 'Lupita, 16',
    location: 'Chiapas',
    text: 'Mi mamá no podía pagar una consulta de nutrición. Aquí me ayudaron a entender cómo alimentarme mejor sin gastar de más.',
    emoji: '🌱',
  },
  {
    name: 'Sofía, 22',
    location: 'Puebla',
    text: 'La doctora me trató con muchísimo respeto y cariño. No sentí que fuera "gratis", sentí que me importaba.',
    emoji: '🌸',
  },
];

const SPECIALTIES = [
  { emoji: '💭', name: 'Psicología', desc: 'Salud mental y bienestar emocional', color: '#F0EEFB' },
  { emoji: '🥗', name: 'Nutrición', desc: 'Alimentación y hábitos saludables', color: '#F0FDF4' },
  { emoji: '🌸', name: 'Ginecología', desc: 'Salud sexual y reproductiva', color: '#FFF1F2' },
  { emoji: '🩺', name: 'Medicina General', desc: 'Consultas de salud general', color: '#EFF6FF' },
  { emoji: '💪', name: 'Entrenamiento', desc: 'Rutinas y acondicionamiento físico', color: '#FFF7ED' },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="faq-item press-scale-card"
      onClick={() => setOpen(!open)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
    >
      <div className="faq-question">
        <span>{question}</span>
        <ChevronDown
          size={18}
          style={{
            color: 'var(--daisies)',
            transition: 'transform 0.3s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            flexShrink: 0,
          }}
        />
      </div>
      {open && (
        <div className="faq-answer animate-fadeIn">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Decoraciones SVG del hero (mismas formas que el landing) ─── */
function SparkleStar({ className = '', size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
      <path
        d="M12 1.5 L13.6 10.4 L22.5 12 L13.6 13.6 L12 22.5 L10.4 13.6 L1.5 12 L10.4 10.4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Triangle({ className = '', size = 10 }) {
  return (
    <svg viewBox="0 0 12 10" width={size} height={(size * 10) / 12} className={className} aria-hidden="true">
      <polygon points="6,0 12,10 0,10" fill="currentColor" />
    </svg>
  );
}

function Scribble({ className = '', width = 80 }) {
  return (
    <svg viewBox="0 0 100 36" width={width} height={(width * 36) / 100} className={className} aria-hidden="true">
      <path
        d="M3 22 Q 14 4, 26 18 Q 36 30, 50 16 Q 62 4, 76 22 Q 86 32, 97 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function LandingView({ onJoinQueue, onJoinWaitlist }) {
  return (
    <div>

      <section className="hero-section">
        <div className="hero-halo" aria-hidden="true" />
        <SparkleStar className="hero-amb-sparkle hero-amb-1" size={22} />
        <Triangle className="hero-amb-tri" size={11} />
        <SparkleStar className="hero-amb-sparkle hero-amb-2" size={18} />

        <div className="hero-container">
          <div className="hero-content animate-fadeInUp">
            <div className="hero-badge">
              <Sparkles size={13} />
              <span>Programa Social Daisies</span>
            </div>
            <h1 className="hero-title">
              Consultas para quienes <span className="hero-title-accent">más las necesitan</span>
            </h1>
            <p className="hero-subtitle">
              Atención de valoración sin costo para comunidades y asociaciones seleccionadas*
            </p>
            <div className="hero-actions">
              <button className="hero-cta-primary press-scale" onClick={onJoinQueue} id="hero-cta">
                Consultas disponibles ahora
                <ChevronRight size={18} />
              </button>
              <button className="hero-cta-secondary press-scale" onClick={onJoinWaitlist} id="secondary-cta">
                Quiero formarme
                <ArrowRight size={16} />
              </button>
            </div>
            <ul className="hero-trust">
              <li>
                <Heart size={15} className="hero-trust-heart" aria-hidden="true" />
                <span><strong>+78,349</strong> consultas</span>
              </li>
              <li aria-hidden="true" className="hero-trust-dot">·</li>
              <li>
                <Star size={15} className="hero-trust-star" aria-hidden="true" />
                <span><strong>4.86</strong> / 5</span>
              </li>
              <li aria-hidden="true" className="hero-trust-dot">·</li>
              <li>
                <ShieldCheck size={15} className="hero-trust-shield" aria-hidden="true" />
                <span><strong>Cédulas profesionales</strong></span>
              </li>
            </ul>
          </div>

          {/* Cluster de fotos juguetón, misma gramática que el hero del landing */}
          <div className="hero-stage animate-fadeInUp-2">
            <div className="hero-glow" aria-hidden="true" />

            <div className="photo-card photo-card-1">
              <img src="/images/hero-fila.jpg" alt="Chica sonriendo al recibir el aviso de su consulta en su teléfono" />
            </div>
            <div className="photo-card photo-card-2">
              <img src="/images/hero-doctora.jpg" alt="Doctora de Daisies sonriendo con bata blanca" />
            </div>
            <div className="photo-card photo-card-3">
              <img src="/images/hero-consulta.jpg" alt="Mujer tomando su consulta por videollamada desde su sala" />
            </div>

            <span className="hero-chip hero-chip-amber">Tranquila</span>
            <span className="hero-chip hero-chip-mint">Escuchada</span>
            <span className="hero-chip hero-chip-tint">Acompañada</span>

            <div className="hero-bubble">
              <img src="/images/flor.png" alt="" aria-hidden="true" />
            </div>

            <div className="hero-minicard">
              <span className="hero-minicard-check" aria-hidden="true">✓</span>
              <div>
                <span className="hero-minicard-kicker">Sin costo</span>
                <span className="hero-minicard-text">Valoración</span>
              </div>
            </div>

            <SparkleStar className="hero-stage-sparkle hero-sp-1" size={20} />
            <SparkleStar className="hero-stage-sparkle hero-sp-2" size={14} />
            <SparkleStar className="hero-stage-sparkle hero-sp-3" size={16} />
            <Triangle className="hero-stage-tri hero-tri-1" size={10} />
            <Triangle className="hero-stage-tri hero-tri-2" size={9} />
            <Scribble className="hero-scribble" width={80} />
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="landing-container">
          <div className="stats-grid">
            <div className="stat-card animate-fadeInUp">
              <div className="stat-icon" style={{ background: 'var(--daisies-50)' }}>
                <Heart size={22} color="var(--daisies)" />
              </div>
              <div className="stat-number">100%</div>
              <div className="stat-label">Gratuito</div>
              <p className="stat-desc">Sin costos ocultos. Siempre gratis.</p>
            </div>
            <div className="stat-card animate-fadeInUp-1">
              <div className="stat-icon" style={{ background: '#F0FDF4' }}>
                <Stethoscope size={22} color="#16A34A" />
              </div>
              <div className="stat-number">5</div>
              <div className="stat-label">Especialidades</div>
              <p className="stat-desc">Psicología, nutrición y más.</p>
            </div>
            <div className="stat-card animate-fadeInUp-2">
              <div className="stat-icon" style={{ background: '#FEF3C7' }}>
                <ShieldCheck size={22} color="#D97706" />
              </div>
              <div className="stat-number">100%</div>
              <div className="stat-label">Profesional</div>
              <p className="stat-desc">Doctoras certificadas de Daisies.</p>
            </div>
            <div className="stat-card animate-fadeInUp-3">
              <div className="stat-icon" style={{ background: '#FFF1F2' }}>
                <Users size={22} color="#E11D48" />
              </div>
              <div className="stat-number">∞</div>
              <div className="stat-label">Para todas</div>
              <p className="stat-desc">Cualquiera que lo necesite.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="how-section" id="como-funciona">
        <div className="landing-container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p className="ds-caption" style={{ marginBottom: 8 }}>CÓMO FUNCIONA</p>
            <h2 className="section-title" style={{ maxWidth: 500, margin: '0 auto' }}>
              Tres pasos para tu consulta gratuita.
            </h2>
          </div>

          {/* Steps detail */}
          <div className="how-steps">
            <div className="how-step-card animate-fadeInUp">
              <div className="how-step-number">1</div>
              <div className="how-step-icon" style={{ background: 'var(--daisies-50)' }}>
                <CalendarCheck size={24} color="var(--daisies)" />
              </div>
              <h3>Regístrate en la fila</h3>
              <p>Llena tus datos básicos y elige la especialidad que necesitas. Te formamos en una fila virtual.</p>
            </div>
            <div className="how-step-card animate-fadeInUp-1">
              <div className="how-step-number">2</div>
              <div className="how-step-icon" style={{ background: '#FEF3C7' }}>
                <Bell size={24} color="#D97706" />
              </div>
              <h3>Recibe una notificación</h3>
              <p>Cuando un horario de consulta quede libre, te avisamos por WhatsApp o correo. ¡Es tu turno!</p>
            </div>
            <div className="how-step-card animate-fadeInUp-2">
              <div className="how-step-icon" style={{ background: '#F0FDF4' }}>
                <div className="how-step-number">3</div>
                <Heart size={24} color="#16A34A" />
              </div>
              <h3>Toma tu consulta</h3>
              <p>Conéctate desde tu celular o computadora. Sin descargas, sin costos. Solo tú y tu doctora.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="for-section">
        <div className="landing-container">
          <div className="for-grid">
            <div className="for-content animate-fadeInUp">
              <p className="ds-caption" style={{ marginBottom: 8 }}>¿PARA QUIÉN ES?</p>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: 'white', letterSpacing: '-0.5px', lineHeight: 1.2, marginBottom: 16 }}>
                Para todas las que lo necesiten.
              </h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, marginBottom: 24 }}>
                Si acceder a un servicio de salud se te ha complicado, esto es para ti.
              </p>
              <div className="for-list">
                <div className="for-item">
                  <span className="for-emoji">🎓</span>
                  <span>Estudiantes sin seguro médico</span>
                </div>
                <div className="for-item">
                  <span className="for-emoji">🏘️</span>
                  <span>Mujeres y niñas de comunidades rurales o indígenas</span>
                </div>
                <div className="for-item">
                  <span className="for-emoji">🤲</span>
                  <span>Personas que no cuentan con recursos para una consulta</span>
                </div>
                <div className="for-item">
                  <span className="for-emoji">🌼</span>
                  <span>Adolescentes que necesitan un espacio seguro</span>
                </div>
                <div className="for-item">
                  <span className="for-emoji">🫂</span>
                  <span>Cualquier persona que lo necesite</span>
                </div>
              </div>
              <button className="btn-white press-scale" onClick={onJoinQueue} style={{ marginTop: 8 }}>
                Quiero formarme
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
        {/* Decorative */}
        <div className="for-deco for-deco-1" />
        <div className="for-deco for-deco-2" />
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* ─── SPECIALTIES ─── */}
      {/* ═══════════════════════════════════════════ */}
      <section className="specialties-section">
        <div className="landing-container">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <p className="ds-caption" style={{ marginBottom: 8 }}>ESPECIALIDADES DISPONIBLES</p>
            <h2 className="section-title">
              Consultas en 5 especialidades.
            </h2>
            <p className="section-body" style={{ maxWidth: 460, margin: '8px auto 0' }}>
              Las mismas doctoras certificadas de Daisies. La misma calidad. Completamente gratis.
            </p>
          </div>
          <div className="specialties-grid">
            {SPECIALTIES.map((s, i) => (
              <div
                key={s.name}
                className={`specialty-card press-scale-card animate-fadeInUp${i > 0 ? `-${i}` : ''}`}
              >
                <div className="specialty-emoji" style={{ background: s.color }}>
                  {s.emoji}
                </div>
                <h4>{s.name}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="landing-container">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 className="section-title">
              Historias que nos inspiran.
            </h2>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                className={`testimonial-card animate-fadeInUp${i > 0 ? `-${i}` : ''}`}
              >
                <div className="testimonial-emoji">{t.emoji}</div>
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} fill="#F59E0B" color="#F59E0B" />
                  ))}
                </div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <strong>{t.name}</strong>
                  <span>{t.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="landing-container" style={{ maxWidth: 640 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 className="section-title">
              ¿Tienes dudas?
            </h2>
          </div>
          <div className="faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      <section className="commitment-section">
        <div className="landing-container" style={{ maxWidth: 640 }}>
          <div className="commitment-card">
            <div className="commitment-icon-chip"><span className="commitment-icon">🤝</span></div>
            <h3>Un compromiso mutuo</h3>
            <p>
              Cada consulta es tiempo real que una doctora dedica a ti. 
              <strong> Es gratis, pero es valioso.</strong> Si la reservas, comprométete a asistir. 
              Si no puedes, avísanos: alguien más la necesita.
            </p>
          </div>
        </div>
      </section>

      <section className="final-cta-section">
        <div className="landing-container" style={{ textAlign: 'center' }}>
          <img src="/images/flor.png" alt="Daisies" className="animate-float" style={{ height: 56, width: 'auto', marginBottom: 16 }} />
          <h2 style={{ fontSize: 28, fontWeight: 800, color: 'white', letterSpacing: '-0.5px', lineHeight: 1.2, marginBottom: 12 }}>
            Mereces atención de calidad.
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 28px' }}>
            Fórmate en la fila virtual y recibe consultas de salud gratuitas cuando haya un horario disponible.
          </p>
          <button className="btn-white press-scale" onClick={onJoinQueue} id="final-cta" style={{ fontSize: 17, padding: '16px 36px' }}>
            Quiero formarme
            <ChevronRight size={20} />
          </button>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 16 }}>
            <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            El proceso toma menos de 2 minutos
          </p>
        </div>
      </section>

      <section className="legal-section" style={{ background: 'var(--surface)', padding: '40px 0' }}>
        <div className="landing-container">
          <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              * Aplica solo para consultas de valoración, primera vez, con una sola especialidad, con la duración básica de membresía. 
              Sujeto a disponibilidad de horarios liberados. Programa social operado por Daisies.
            </p>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-top">
            <div className="footer-brand">
              <img src="/images/flor.png" alt="Daisies" style={{ height: 32, width: 'auto' }} />
              <img src="/images/letras.png" alt="Daisies" style={{ height: 22, width: 'auto', filter: 'brightness(0) invert(1)' }} />
            </div>
            <div className="footer-links">
              <a href="https://daisies.mx" target="_blank" rel="noopener noreferrer">daisies.mx</a>
              <a href="https://daisies.mx/terminos-y-condiciones" target="_blank" rel="noopener noreferrer">Términos y condiciones</a>
              <a href="https://daisies.mx/aviso-privacidad" target="_blank" rel="noopener noreferrer">Aviso de privacidad</a>
              <a href="mailto:soporte@daisies.mx">Contacto</a>
            </div>
          </div>
          <div className="footer-divider" />
          <p className="footer-copy">
            💜 Hecho con amor para quienes más lo necesitan · © 2026 Daisies
          </p>
        </div>
      </footer>
    </div>
  );
}
